import { useEffect, useState } from 'react';
import { Card, Button, Spinner, Badge } from '../components/ui';
import CancelSubscriptionModal from './CancelSubscriptionModal';

const PLAN_FEATURES = {
  starter:   ['Certificate tracking', 'Right to Rent', 'AI tenancy checker', 'Email reminders', 'Email support'],
  landlord:  ['Everything in Starter', 'HMO licensing checker', 'Compliance reports', 'Priority email support'],
  portfolio: ['Everything in Landlord', 'Phone support', 'Dedicated account manager', 'Custom onboarding'],
};

const PLAN_COLORS = {
  starter:   'var(--ink-2)',
  landlord:  'var(--accent)',
  portfolio: 'var(--ink)',
};

const PLAN_LABELS = { trial:'Free Trial', starter:'Starter', landlord:'Landlord', portfolio:'Portfolio' };
const STATUS_COLORS = { trialing:'valid', active:'valid', past_due:'expired', canceled:'expired' };

function formatDate(dateStr) {
  if (!dateStr) return null;
  return new Date(dateStr).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
}

function formatPrice(pencePerMonth) {
  return `£${(pencePerMonth / 100).toFixed(0)}`;
}

function formatLimit(limit) {
  return limit >= 999 ? 'Unlimited' : `Up to ${limit}`;
}

export default function Billing() {
  const [status, setStatus]     = useState(null);
  const [plans, setPlans]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [upgrading, setUpgrading] = useState('');
  const [showCancelModal, setShowCancelModal] = useState(false);

  function billingHeaders() {
    return { Authorization: `Bearer ${localStorage.getItem('token')}`, ...(localStorage.getItem('lc_access_code') ? {'X-Access-Code': localStorage.getItem('lc_access_code')} : {}) };
  }

  function loadStatus() {
    return fetch('/api/billing/status', { headers: billingHeaders() }).then(r => r.json());
  }

  useEffect(() => {
    Promise.all([
      loadStatus(),
      fetch('/api/plans').then(r => r.json()),
    ]).then(([s, p]) => {
      setStatus(s);
      setPlans(p || []);
      setLoading(false);
    });
  }, []);

  async function handleUpgrade(planId) {
    setUpgrading(planId);
    try {
      const res = await fetch('/api/billing/create-checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify({ plan: planId }),
      });
      const data = await res.json();
      if (data.url) window.location.href = data.url;
    } finally { setUpgrading(''); }
  }

  async function handleManage() {
    const res = await fetch('/api/billing/portal', {
      method: 'POST',
      headers: { Authorization: `Bearer ${localStorage.getItem('token')}`, ...(localStorage.getItem('lc_access_code') ? {'X-Access-Code': localStorage.getItem('lc_access_code')} : {}) },
    });
    const data = await res.json();
    if (data.url) window.location.href = data.url;
  }

  if (loading) return <Spinner />;

  const isTrial   = status?.is_trial;
  const isExpired = isTrial && status?.trial_days_left === 0;

  return (
    <div className="fade-up">
      <div style={{ marginBottom: 32 }}>
        <h1 style={{ fontSize: 32, fontWeight: 300 }}>Billing & <em>Subscription</em></h1>
        <p style={{ color: 'var(--ink-3)', marginTop: 4 }}>Manage your plan and payment details</p>
      </div>

      {/* Current plan status */}
      <Card style={{ marginBottom: 32 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 16 }}>
          <div>
            <p style={{ fontSize: 13, color: 'var(--ink-3)', marginBottom: 4 }}>Current plan</p>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
              <h2 style={{ fontSize: 26, fontWeight: 300 }}>{PLAN_LABELS[status?.plan] || 'Free Trial'}</h2>
              <Badge status={STATUS_COLORS[status?.sub_status] || 'valid'} label={status?.sub_status || 'trialing'} />
            </div>
            {isTrial && status?.trial_days_left > 0 && (
              <p style={{ fontSize: 14, color: 'var(--warn)' }}>
                ⏳ {status.trial_days_left} day{status.trial_days_left !== 1 ? 's' : ''} left in your free trial
              </p>
            )}
            {isExpired && (
              <p style={{ fontSize: 14, color: 'var(--danger)' }}>
                🚨 Your free trial has expired — upgrade to continue
              </p>
            )}
            {!isTrial && status?.renews_at && status?.sub_status === 'active' && !status?.cancel_at_period_end && (
              <p style={{ fontSize: 13, color: 'var(--ink-3)', marginTop: 4 }}>
                🔄 Renews on {formatDate(status.renews_at)}
              </p>
            )}
            {!isTrial && status?.cancel_at_period_end && status?.renews_at && (
              <p style={{ fontSize: 13, color: 'var(--warn)', marginTop: 4 }}>
                ⚠️ Cancels on {formatDate(status.renews_at)} — you have access until then
              </p>
            )}
            {!isTrial && status?.sub_status === 'past_due' && (
              <p style={{ fontSize: 13, color: 'var(--danger)', marginTop: 4 }}>
                ⚠️ Payment overdue — please update your payment details
              </p>
            )}
            <p style={{ fontSize: 13, color: 'var(--ink-3)', marginTop: 8 }}>
              {status?.property_count} / {status?.property_limit === 999999 ? '∞' : status?.property_limit} properties used
            </p>
            {!status?.ai_enabled && (
              <p style={{ fontSize: 13, color: 'var(--warn)', marginTop: 4 }}>
                ⚠️ AI tenancy checker disabled on free trial — upgrade to enable
              </p>
            )}
          </div>
          {!isTrial && (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 8 }}>
              <Button variant="ghost" onClick={handleManage}>Manage billing →</Button>
              {!status?.cancel_at_period_end && status?.sub_status !== 'past_due' && (
                <button
                  onClick={() => setShowCancelModal(true)}
                  style={{ background: 'none', border: 'none', color: 'var(--ink-3)', fontSize: 12, cursor: 'pointer', textDecoration: 'underline' }}
                >
                  Cancel subscription
                </button>
              )}
            </div>
          )}
        </div>

        {/* Property usage bar */}
        {status?.property_limit < 999 && (
          <div style={{ marginTop: 16 }}>
            <div style={{ height: 4, background: 'var(--border)', borderRadius: 2 }}>
              <div style={{
                height: '100%',
                width: `${Math.min(100, (status?.property_count / status?.property_limit) * 100)}%`,
                background: status?.property_count >= status?.property_limit ? 'var(--danger)' : 'var(--accent)',
                borderRadius: 2, transition: 'width .3s',
              }} />
            </div>
            {status?.property_count >= status?.property_limit && (
              <p style={{ fontSize: 12, color: 'var(--danger)', marginTop: 4 }}>
                Property limit reached — upgrade to add more
              </p>
            )}
          </div>
        )}
      </Card>

      {/* Plan cards — loaded from API */}
      {(isTrial || status?.plan !== 'portfolio') && plans.length > 0 && (
        <>
          <h3 style={{ fontSize: 18, marginBottom: 20 }}>
            {isTrial ? 'Choose a plan to continue' : 'Upgrade your plan'}
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 20, marginBottom: 32 }}>
            {plans.filter(p => isTrial || p.id !== status?.plan).map(plan => (
              <Card key={plan.id} style={{
                borderColor: plan.id === 'landlord' ? 'var(--accent)' : 'var(--border)',
                borderWidth: plan.id === 'landlord' ? 2 : 1.5,
                position: 'relative',
              }}>
                {plan.id === 'landlord' && (
                  <div style={{ position: 'absolute', top: -12, left: '50%', transform: 'translateX(-50%)', background: 'var(--accent)', color: '#fff', fontSize: 11, fontWeight: 600, padding: '3px 12px', borderRadius: 20 }}>
                    MOST POPULAR
                  </div>
                )}
                <p style={{ fontWeight: 600, fontSize: 16, marginBottom: 4, color: PLAN_COLORS[plan.id] }}>{plan.name}</p>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 2, marginBottom: 4 }}>
                  <span style={{ fontSize: 28, fontFamily: 'var(--font-serif)', fontWeight: 300 }}>{formatPrice(plan.price_gbp)}</span>
                  <span style={{ fontSize: 13, color: 'var(--ink-3)' }}>/month</span>
                </div>
                <p style={{ fontSize: 13, color: 'var(--ink-3)', marginBottom: 16 }}>
                  {formatLimit(plan.property_limit)} properties
                </p>
                <ul style={{ listStyle: 'none', marginBottom: 20 }}>
                  {(PLAN_FEATURES[plan.id] || []).map((f, i) => (
                    <li key={i} style={{ fontSize: 13, color: 'var(--ink-2)', marginBottom: 6 }}>✓ {f}</li>
                  ))}
                </ul>
                <Button
                  onClick={() => handleUpgrade(plan.id)}
                  loading={upgrading === plan.id}
                  variant={plan.id === 'landlord' ? 'primary' : 'ghost'}
                  style={{ width: '100%', justifyContent: 'center' }}
                >
                  {status?.plan === plan.id ? 'Current plan' : `Upgrade to ${plan.name}`}
                </Button>
              </Card>
            ))}
          </div>
        </>
      )}

      <p style={{ fontSize: 12, color: 'var(--ink-3)', textAlign: 'center' }}>
        All plans include a 14-day free trial. Cancel anytime. Prices exclude VAT.
      </p>
      <p style={{ fontSize: 13, color: 'var(--ink-3)', textAlign: 'center', marginTop: 12 }}>
        Managing more than 50 properties?{' '}
        <a href="mailto:contact@landlordcomply.uk" style={{ color: 'var(--accent)' }}>Contact us</a>
        {' '}for an enterprise plan.
      </p>

      <CancelSubscriptionModal
        isOpen={showCancelModal}
        onClose={() => setShowCancelModal(false)}
        onCancelled={() => loadStatus().then(setStatus)}
      />
    </div>
  );
}